import { useEffect, useRef, useState } from "react";
import { Box, Grid, IconButton } from "@mui/material";
import SectionHeader from "../SectionHeader";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import styles from "./styles";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

const buildSoundCloudUrl = (url) =>
  `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&visual=true&auto_play=false&show_related=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false`;

const SoundCloudWidgets = ({ recordings, description = "Below you will find recordings from this artist" }) => {
  const iframeRefs = useRef([]);
  const widgetsRef = useRef([]);
  const swiperRef = useRef(null);
  const [needsNav, setNeedsNav] = useState(false);

  const updateNav = (swiper) => setNeedsNav(!(swiper.isBeginning && swiper.isEnd));

  useEffect(() => {
    if (!recordings.length) return;

    const initWidgets = () => {
      if (!window.SC) return;

      widgetsRef.current = iframeRefs.current
        .filter(Boolean)
        .map((iframe) => window.SC.Widget(iframe));

      widgetsRef.current.forEach((widget, index) => {
        widget.bind(window.SC.Widget.Events.PLAY, () => {
          widgetsRef.current.forEach((other, i) => {
            if (i !== index) other.pause();
          });
        });
      });
    };

    if (window.SC) {
      initWidgets();
    } else {
      const timer = setTimeout(initWidgets, 1500);
      return () => clearTimeout(timer);
    }

    return () => {
      widgetsRef.current.forEach((widget) => {
        if (widget) {
          try { widget.unbind(window.SC.Widget.Events.PLAY); } catch (_) {}
        }
      });
    };
  }, [recordings]);

  if (!recordings.length) return null;

  return (
    <Grid container justifyContent="center">
      <Grid xs={10} mt={10} item>
        <SectionHeader title="Soundcloud" description={description} />
        <Box sx={styles.wrapperSx}>
          <Swiper
            onSwiper={(swiper) => { swiperRef.current = swiper; }}
            onAfterInit={updateNav}
            onResize={updateNav}
            centerInsufficientSlides={true}
            grabCursor={true}
            spaceBetween={24}
            slidesPerView="auto"
          >
            {recordings.map((url, index) => (
              <SwiperSlide key={url} style={styles.slideStyle}>
                <iframe
                  ref={(el) => { iframeRefs.current[index] = el; }}
                  title={`SoundCloud Widget ${index + 1}`}
                  width="100%"
                  height="380"
                  allow="autoplay; encrypted-media"
                  src={buildSoundCloudUrl(url)}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>

        {needsNav && (
          <Box sx={styles.navRowSx}>
            <IconButton onClick={() => swiperRef.current?.slidePrev()} sx={styles.navButtonSx}>
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton onClick={() => swiperRef.current?.slideNext()} sx={styles.navButtonSx}>
              <NavigateNextIcon />
            </IconButton>
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

export default SoundCloudWidgets;
